/**
 * Ontology v2 read-flip (Phase 4) — map the persistent-graph v2 projection
 * returned by `graph.conversationGraph.getV2` into Cytoscape ElementDefinitions
 * for the chat investigation-graph panel.
 *
 * This is the single home for the v2 display palette. The renderer
 * (ObsidianGraph) stays generic: it reads the precomputed `layerColor` on nodes
 * and `lineColor` / `dashed` on edges when present, and falls back to its v1
 * type-based colouring otherwise. Colour/style choices mirror the standalone
 * reference renderer `scripts/plot-v2-only.mjs`.
 *
 * Spec: core/part4 §2.2.16 "Ontology v2 read-flip (Phase 4)".
 */
import type { ElementDefinition } from 'cytoscape';

/** v2 layer → node background colour. */
export const V2_LAYER_COLOR: Record<string, string> = {
  identity: '#4EDBA8',
  infrastructure: '#5AAEF5',
  application: '#A89FF0',
  data: '#F08050',
  activity: '#E8A840',
  threat: '#F07070',
};

/** v2 edge category → line colour + dashed flag. */
export const V2_CATEGORY_STYLE: Record<string, { line: string; dashed: boolean }> = {
  directory: { line: '#666666', dashed: false },
  capability: { line: '#b83227', dashed: false },
  activity: { line: '#E8A840', dashed: false },
  judgment: { line: '#A89FF0', dashed: true },
};

const FALLBACK_NODE_COLOR = '#9aa0a6';
const FALLBACK_EDGE_COLOR = '#999999';

export interface V2GraphNode {
  id: string;
  v1Type: string;
  v2Type: string | null;
  v2Kind: string | null;
  v2Layer: string | null;
  canonicalName: string;
  identityKeys: unknown;
  properties: unknown;
}

export interface V2GraphEdge {
  id: string;
  source: string;
  target: string;
  v1Type: string;
  v2Type: string | null;
  v2Category: string | null;
}

export interface V2GraphResponse {
  nodes: V2GraphNode[];
  edges: V2GraphEdge[];
}

/**
 * Convert a v2 conversation-graph projection into ElementDefinitions.
 *
 * Node `data.type` stays the v1 entity type so the renderer's glyph/icon
 * selection keeps working; `layerColor` overrides the fill so the node renders
 * in its v2-layer colour. `sub` shows `v2Type/v2Kind`. The full v2 fields plus
 * `identityKeys` ride along in `data` for the node-details popover.
 */
export function v2GraphToElements(graph: V2GraphResponse): ElementDefinition[] {
  const els: ElementDefinition[] = [];

  for (const n of graph.nodes) {
    const layer = n.v2Layer ?? '';
    const layerColor = V2_LAYER_COLOR[layer] ?? FALLBACK_NODE_COLOR;
    const subtitle = n.v2Type
      ? (n.v2Kind ? `${n.v2Type}/${n.v2Kind}` : n.v2Type)
      : undefined;
    const props = (n.properties && typeof n.properties === 'object')
      ? (n.properties as Record<string, unknown>)
      : {};
    els.push({
      group: 'nodes',
      data: {
        ...props,
        id: n.id,
        label: n.canonicalName,
        // v1 type drives glyph/icon; layerColor drives the v2 fill.
        type: n.v1Type,
        sub: subtitle,
        layerColor,
        v2Type: n.v2Type ?? undefined,
        v2Kind: n.v2Kind ?? undefined,
        v2Layer: n.v2Layer ?? undefined,
        identityKeys: n.identityKeys ?? undefined,
      },
    });
  }

  for (const e of graph.edges) {
    const cat = e.v2Category ?? '';
    const style = V2_CATEGORY_STYLE[cat];
    // Edge label surfaces the v2 relationship + category (no edge-click subsystem
    // exists in the panel today; the hover label is the affordance).
    const label = e.v2Type
      ? (e.v2Category ? `${e.v2Type} · ${e.v2Category}` : e.v2Type)
      : e.v1Type;
    els.push({
      group: 'edges',
      data: {
        id: e.id,
        source: e.source,
        target: e.target,
        type: e.v1Type,
        label,
        v2Type: e.v2Type ?? undefined,
        v2Category: e.v2Category ?? undefined,
        lineColor: style?.line ?? FALLBACK_EDGE_COLOR,
        dashed: style?.dashed ?? false,
      },
    });
  }

  return els;
}
